import "./BudgetDashboard.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import {
  ComposedChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  createBudget,
  createBudgetCategory,
  createBudgetEntry,
  deleteBudgetCategory,
  deleteBudgetEntry,
  getBudgetDashboard,
  getBudgets,
  getBudgetSources,
  rebuildBudgetRevenues,
  updateBudget,
  updateBudgetEntry,
} from "../../../utility/budgetApi";
import {
  deleteBudgetAttachment,
  uploadBudgetAttachment,
} from "../../../utility/uploadBudgetAttachment";
import { analyzeBudgetDocument } from "../../../utility/aiApi";

const SOURCE_COLORS = {
  pos: "#4d72ff",
  ticketing: "#a873ff",
  manual: "#27ae60",
};

const DEFAULT_BUDGET_CATEGORIES = [
  ["food-drinks", "Nourriture et boissons", "#ff7a00"],
  ["administration", "Administration", "#f2c94c"],
  ["sponsors", "Sponsors", "#27ae60"],
  ["transport", "Transport", "#2d9cdb"],
  ["electronics", "Électronique", "#4d72ff"],
  ["telecom", "Téléphone et Internet", "#56ccf2"],
  ["insurance", "Assurances", "#bb6bd9"],
  ["containers", "Gobelets et contenants", "#eb5757"],
  ["entertainment", "DJ et sonorisation", "#9b51e0"],
  ["venue", "Salles et chapiteaux", "#2f80ed"],
  ["decorations", "Décorations", "#f302ca"],
  ["clothing", "Vêtements", "#e25252"],
  ["tickets", "Tickets et bracelets", "#8ba4bf"],
  ["cleaning", "Nettoyage", "#6fcf97"],
  ["supplies", "Fournitures et matériel", "#c28c4d"],
  ["other", "Autre", "#7c8eaa"],
].map(([id, name, color], order) => ({ id, name, color, order }));
const DEFAULT_CATEGORY_IDS = new Set(DEFAULT_BUDGET_CATEGORIES.map((category) => category.id));
const CATEGORY_COLORS = [
  "#20d7ff",
  "#4d72ff",
  "#8b5bff",
  "#e84bcb",
  "#f0647e",
  "#ff9f43",
  "#f2c94c",
  "#41c98e",
];
const isCustomCategory = (category) =>
  category?.isCustom === true || !DEFAULT_CATEGORY_IDS.has(category?.id);

const euro = new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" });
const formatMoney = (cents) => euro.format((Number(cents) || 0) / 100);
const toInputDate = (value) => new Date(Number(value) || Date.now()).toISOString().slice(0, 10);
const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
const randomId = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const emptyEntry = (username = "") => ({
  type: "expense",
  providerName: "",
  date: toInputDate(Date.now()),
  categoryId: "",
  lines: [{ id: randomId(), label: "", amount: "" }],
  creatorName: username,
  attachment: null,
});

const Icon = ({ name }) => <i className={`fas fa-${name}`} aria-hidden="true" />;

function SlideOver({ open, title, onClose, children, wide = false }) {
  if (!open) return null;
  return (
    <div className="budget-drawer-layer" role="presentation" onMouseDown={onClose}>
      <aside
        className={`budget-drawer ${wide ? "budget-drawer-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <h2>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Fermer">
            <Icon name="times" />
          </button>
        </header>
        {children}
      </aside>
    </div>
  );
}

function Metric({ label, value, tone, icon }) {
  return (
    <div className={`budget-metric budget-metric-${tone}`}>
      <span className="budget-metric-icon">
        <Icon name={icon} />
      </span>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function CategoryPickerModal({
  open,
  categories,
  selectedId,
  onSelect,
  onClose,
  onCreate,
  creating,
}) {
  const { t } = useTranslation("budget");
  const [name, setName] = useState("");
  const [color, setColor] = useState(CATEGORY_COLORS[2]);

  useEffect(() => {
    if (!open) return;
    setName("");
    setColor(CATEGORY_COLORS[2]);
  }, [open]);

  if (!open) return null;
  return (
    <div className="category-modal-layer" role="presentation" onMouseDown={onClose}>
      <section
        className="category-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="category-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <h3 id="category-modal-title">{t("choose_category")}</h3>
            <p>{t("choose_category_help")}</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label={t("close")}>
            <Icon name="times" />
          </button>
        </header>
        <div className="category-card-grid">
          {categories.map((category) => (
            <button
              type="button"
              key={category.id}
              className={`category-card ${selectedId === category.id ? "selected" : ""}`}
              style={{ "--category-color": category.color || "#7c8eaa" }}
              onClick={() => {
                onSelect(category.id);
                onClose();
              }}
            >
              <span className="category-card-icon">
                <Icon name="tag" />
              </span>
              <span>
                <strong>{category.name}</strong>
                <small>
                  {isCustomCategory(category) ? t("custom_category") : t("default_category")}
                </small>
              </span>
              {selectedId === category.id ? <Icon name="check-circle" /> : null}
            </button>
          ))}
        </div>
        <form
          className="category-create-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const category = await onCreate({ name, color });
            if (category) {
              setName("");
              onClose();
            }
          }}
        >
          <div>
            <h4>{t("create_category")}</h4>
            <p>{t("create_category_help")}</p>
          </div>
          <label>
            {t("category_name")}
            <input
              value={name}
              maxLength={50}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("category_name_placeholder")}
            />
          </label>
          <div className="category-color-field">
            <span>{t("category_color")}</span>
            <div>
              {CATEGORY_COLORS.map((option) => (
                <button
                  type="button"
                  key={option}
                  className={color === option ? "selected" : ""}
                  style={{ backgroundColor: option }}
                  onClick={() => setColor(option)}
                  aria-label={option}
                />
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="primary-button"
            disabled={creating || name.trim().length < 2}
          >
            <Icon name="plus" /> {creating ? t("creating_category") : t("create_category")}
          </button>
        </form>
      </section>
    </div>
  );
}

function EntryForm({ open, entry, budget, categories, onClose, onSaved, onCategoriesChanged }) {
  const { t } = useTranslation("budget");
  const alert = useAlert();
  const user = useSelector((state) => state.userReducer.users);
  const [form, setForm] = useState(emptyEntry(user?.username || user?.email || ""));
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState("");
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState(categories);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [draftEntryId, setDraftEntryId] = useState(randomId());
  const [analyzingDocument, setAnalyzingDocument] = useState(false);
  const [analysisWarnings, setAnalysisWarnings] = useState([]);
  const [draftAttachment, setDraftAttachment] = useState(null);
  const uploadRef = useRef(null);
  const isEditing = Boolean(entry?.id);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setUploadProgress(0);
    setCategoryModalOpen(false);
    setDraftEntryId(entry?.id || randomId());
    setAnalysisWarnings([]);
    setDraftAttachment(null);
    setForm(
      entry
        ? {
            ...entry,
            date: toInputDate(entry.date),
            lines: entry.lines.map((line) => ({
              ...line,
              amount: (line.amountCents / 100).toFixed(2),
            })),
          }
        : emptyEntry(user?.username || user?.email || ""),
    );
  }, [open, entry, user?.username, user?.email]);

  useEffect(() => {
    if (open) setAvailableCategories(categories);
  }, [open, categories]);

  useEffect(() => {
    if (!file) {
      setAttachmentPreview(form.attachment?.downloadUrl || "");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setAttachmentPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, form.attachment?.downloadUrl]);

  const totalCents = useMemo(
    () =>
      form.lines.reduce(
        (sum, line) => sum + Math.round((Number(String(line.amount).replace(",", ".")) || 0) * 100),
        0,
      ),
    [form.lines],
  );

  const setLine = (index, key, value) =>
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [key]: value } : line,
      ),
    }));

  const selectAndAnalyzeDocument = async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setAnalyzingDocument(true);
    setAnalysisWarnings([]);
    try {
      if (draftAttachment?.storagePath) {
        await deleteBudgetAttachment(draftAttachment.storagePath);
      }
      uploadRef.current = uploadBudgetAttachment({
        file: selectedFile,
        orgId: budget.ownerOrgId,
        budgetId: budget.id,
        entryId: draftEntryId,
        onProgress: setUploadProgress,
      });
      const attachment = await uploadRef.current;
      uploadRef.current = null;
      setDraftAttachment(attachment);
      setForm((current) => ({ ...current, attachment }));
      setFile(null);
      const { analysis } = await analyzeBudgetDocument(budget.id, {
        entryId: draftEntryId,
        attachment,
      });
      setAnalysisWarnings(analysis.warnings || []);
      setForm((current) => ({
        ...current,
        providerName: analysis.providerName || current.providerName,
        date: analysis.date || current.date,
        categoryId: analysis.categoryId || current.categoryId,
        attachment,
        lines:
          analysis.description && analysis.totalAmountCents
            ? [
                {
                  id: randomId(),
                  label: analysis.description,
                  amount: (analysis.totalAmountCents / 100).toFixed(2),
                },
              ]
            : current.lines,
      }));
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      uploadRef.current = null;
      setAnalyzingDocument(false);
    }
  };

  const closeWithCleanup = async () => {
    if (draftAttachment?.storagePath) await deleteBudgetAttachment(draftAttachment.storagePath);
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    const invalidLine = form.lines.some(
      (line) => !line.label.trim() || !(Number(String(line.amount).replace(",", ".")) > 0),
    );
    if (!form.providerName.trim() || !form.categoryId || invalidLine) {
      alert.show(t("complete_required_fields"), { type: "error" });
      return;
    }

    setSaving(true);
    try {
      const entryId = entry?.id || draftEntryId;
      let attachment = form.attachment || null;
      if (file) {
        uploadRef.current = uploadBudgetAttachment({
          file,
          orgId: budget.ownerOrgId,
          budgetId: budget.id,
          entryId,
          onProgress: setUploadProgress,
        });
        attachment = await uploadRef.current;
        uploadRef.current = null;
      }
      const payload = {
        id: entryId,
        type: form.type,
        providerName: form.providerName,
        date: Date.parse(`${form.date}T12:00:00`),
        categoryId: form.categoryId,
        creatorName: form.creatorName,
        attachment,
        lines: form.lines.map((line) => ({
          id: line.id,
          label: line.label.trim(),
          amountCents: Math.round(Number(String(line.amount).replace(",", ".")) * 100),
        })),
      };
      if (isEditing) await updateBudgetEntry(budget.id, entry.id, payload);
      else await createBudgetEntry(budget.id, payload);
      setDraftAttachment(null);
      alert.show(t("entry_saved"), { type: "success" });
      onSaved();
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      uploadRef.current = null;
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    setSaving(true);
    try {
      await deleteBudgetEntry(budget.id, entry.id);
      alert.show(t("entry_deleted"), { type: "success" });
      onSaved();
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const createCategory = async ({ name, color }) => {
    setCreatingCategory(true);
    try {
      const response = await createBudgetCategory(budget.id, { name, color });
      setAvailableCategories((current) => [...current, response.category]);
      setForm((current) => ({ ...current, categoryId: response.category.id }));
      alert.show(t("category_created"), { type: "success" });
      onCategoriesChanged?.();
      return response.category;
    } catch (error) {
      alert.show(error.message, { type: "error" });
      return null;
    } finally {
      setCreatingCategory(false);
    }
  };

  const selectedCategory = availableCategories.find((category) => category.id === form.categoryId);
  const attachmentField = (
    <section className="attachment-field ai-expense-analysis">
      {analyzingDocument ? (
        <div className="ai-document-loading" role="status" aria-live="polite">
          <span className="ai-document-spinner" aria-hidden="true" />
          <strong>{t("ai_analyzing_document")}</strong>
          <small>{t("ai_analyzing_help")}</small>
        </div>
      ) : null}
      <div className="attachment-heading">
        <span className="ai-analysis-label">
          <Icon name="magic" /> {t("ai_expense_analysis")}
        </span>
        <strong>{t("attachment")}</strong>
        <span>{t("ai_attachment_help")}</span>
      </div>
      <input
        id="budget-attachment"
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={(event) => selectAndAnalyzeDocument(event.target.files?.[0] || null)}
        disabled={analyzingDocument}
      />
      <label htmlFor="budget-attachment" className="attachment-drop">
        <Icon name="cloud-upload-alt" />
        <span>{file?.name || form.attachment?.fileName || t("choose_file")}</span>
      </label>
      {analysisWarnings.length ? (
        <p className="analysis-warning">{t("ai_review_warning")}</p>
      ) : null}
      {attachmentPreview ? (
        <div className="attachment-preview">
          {(file?.type || form.attachment?.mimeType) === "application/pdf" ? (
            <iframe src={attachmentPreview} title={t("attachment")} />
          ) : (
            <img
              src={attachmentPreview}
              alt={file?.name || form.attachment?.fileName || t("attachment")}
            />
          )}
        </div>
      ) : null}
      {form.attachment?.downloadUrl ? (
        <a href={form.attachment.downloadUrl} target="_blank" rel="noreferrer">
          <Icon name="external-link-alt" /> {t("view_attachment")}
        </a>
      ) : null}
      {file || form.attachment ? (
        <button
          type="button"
          className="upload-cancel"
          onClick={() => {
            if (draftAttachment?.storagePath) deleteBudgetAttachment(draftAttachment.storagePath);
            setFile(null);
            setDraftAttachment(null);
            setForm((current) => ({ ...current, attachment: null }));
          }}
        >
          {t("remove_attachment")}
        </button>
      ) : null}
      {uploadProgress > 0 && uploadProgress < 100 ? (
        <>
          <div className="upload-progress">
            <span style={{ width: `${uploadProgress}%` }} />
          </div>
          <button
            type="button"
            className="upload-cancel"
            onClick={() => uploadRef.current?.cancel()}
          >
            {t("cancel_upload")}
          </button>
        </>
      ) : null}
    </section>
  );

  return (
    <SlideOver
      open={open}
      title={isEditing ? t("edit_entry") : t("new_entry")}
      onClose={closeWithCleanup}
    >
      <form className="budget-entry-form" onSubmit={submit}>
        <div className="entry-type-switch" aria-label={t("entry_type")}>
          {["expense", "revenue"].map((type) => (
            <button
              type="button"
              key={type}
              className={form.type === type ? "active" : ""}
              onClick={() => setForm((current) => ({ ...current, type }))}
            >
              {t(type)}
            </button>
          ))}
        </div>
        {attachmentField}
        <label>
          {t("provider")}
          <input
            value={form.providerName}
            onChange={(event) => setForm({ ...form, providerName: event.target.value })}
            placeholder={t("provider_placeholder")}
          />
        </label>
        <div className="budget-form-grid">
          <label>
            {t("date")}
            <input
              type="date"
              value={form.date}
              onChange={(event) => setForm({ ...form, date: event.target.value })}
            />
          </label>
          <label>
            {t("category")}
            <button
              type="button"
              className={`category-picker-trigger ${selectedCategory ? "selected" : ""}`}
              style={{ "--category-color": selectedCategory?.color || "#7c8eaa" }}
              onClick={() => setCategoryModalOpen(true)}
            >
              <span className="category-picker-icon">
                <Icon name="shapes" />
              </span>
              <span>
                <strong>{selectedCategory?.name || t("choose_category")}</strong>
                <small>{selectedCategory ? t("change_category") : t("choose_category_help")}</small>
              </span>
              <Icon name="chevron-right" />
            </button>
          </label>
        </div>
        <section className="entry-lines">
          <div className="section-heading">
            <h3>{t("details")}</h3>
            <button
              type="button"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  lines: [...current.lines, { id: randomId(), label: "", amount: "" }],
                }))
              }
            >
              <Icon name="plus" /> {t("add_line")}
            </button>
          </div>
          {form.lines.map((line, index) => (
            <div className="entry-line" key={line.id}>
              <input
                value={line.label}
                onChange={(event) => setLine(index, "label", event.target.value)}
                placeholder={t("line_label")}
              />
              <div className="money-input">
                <input
                  inputMode="decimal"
                  value={line.amount}
                  onChange={(event) => setLine(index, "amount", event.target.value)}
                  placeholder="0,00"
                />
                <span>€</span>
              </div>
              <button
                type="button"
                className="icon-button"
                disabled={form.lines.length === 1}
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    lines: current.lines.filter((_, lineIndex) => lineIndex !== index),
                  }))
                }
              >
                <Icon name="trash" />
              </button>
            </div>
          ))}
          <div className="entry-total">
            <span>{t("total")}</span>
            <strong>{formatMoney(totalCents)}</strong>
          </div>
        </section>
        <label>
          {t("creator")}
          <input
            value={form.creatorName}
            onChange={(event) => setForm({ ...form, creatorName: event.target.value })}
          />
        </label>
        <footer>
          {isEditing ? (
            <button type="button" className="danger-button" onClick={remove} disabled={saving}>
              <Icon name="trash" /> {t("delete")}
            </button>
          ) : (
            <span />
          )}
          <div>
            <button type="button" className="secondary-button" onClick={closeWithCleanup}>
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={saving || analyzingDocument}
            >
              {saving ? t("saving") : t("save")}
            </button>
          </div>
        </footer>
      </form>
      <CategoryPickerModal
        open={categoryModalOpen}
        categories={availableCategories}
        selectedId={form.categoryId}
        onSelect={(categoryId) => setForm((current) => ({ ...current, categoryId }))}
        onClose={() => setCategoryModalOpen(false)}
        onCreate={createCategory}
        creating={creatingCategory}
      />
    </SlideOver>
  );
}

function BudgetForm({ open, budget, organizations, events, ownerOrgId, onClose, onSaved }) {
  const { t } = useTranslation("budget");
  const alert = useAlert();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const categories = budget?.categories?.length ? budget.categories : DEFAULT_BUDGET_CATEGORIES;
    setForm({
      name: budget?.name || "",
      startDate: toInputDate(budget?.startDate || Date.now()),
      endDate: toInputDate(budget?.endDate || new Date().setFullYear(new Date().getFullYear() + 1)),
      includedPosOrgIds: budget?.includedPosOrgIds || [ownerOrgId].filter(Boolean),
      includedEventIds: budget?.includedEventIds || [],
      plannedAmountsByCategory: Object.fromEntries(
        categories.map((category) => [
          category.id,
          budget?.plannedAmountsByCategory?.[category.id]
            ? String(budget.plannedAmountsByCategory[category.id] / 100)
            : "",
        ]),
      ),
      categories,
    });
  }, [open, budget, ownerOrgId]);

  if (!form) return null;
  const toggle = (key, value) =>
    setForm((current) => ({
      ...current,
      [key]: current[key].includes(value)
        ? current[key].filter((item) => item !== value)
        : [...current[key], value],
    }));

  const plannedTotalCents = form.categories.reduce((total, category) => {
    const amount = Number(
      String(form.plannedAmountsByCategory[category.id] || "").replace(",", "."),
    );
    return total + (Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0);
  }, 0);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        ownerOrgId,
        startDate: Date.parse(`${form.startDate}T00:00:00`),
        endDate: Date.parse(`${form.endDate}T23:59:59`),
        plannedAmountsByCategory: Object.fromEntries(
          form.categories.flatMap((category) => {
            const amount = Number(
              String(form.plannedAmountsByCategory[category.id] || "").replace(",", "."),
            );
            return Number.isFinite(amount) && amount > 0
              ? [[category.id, Math.round(amount * 100)]]
              : [];
          }),
        ),
      };
      const response = budget
        ? await updateBudget(budget.id, payload)
        : await createBudget(payload);
      const sourcesChanged =
        !budget ||
        Number(budget.startDate) !== Number(payload.startDate) ||
        Number(budget.endDate) !== Number(payload.endDate) ||
        JSON.stringify([...(budget.includedPosOrgIds || [])].sort()) !==
          JSON.stringify([...form.includedPosOrgIds].sort()) ||
        JSON.stringify([...(budget.includedEventIds || [])].sort()) !==
          JSON.stringify([...form.includedEventIds].sort());
      if (sourcesChanged) {
        try {
          await rebuildBudgetRevenues(response.budget.id, {});
        } catch (rebuildError) {
          alert.show(rebuildError.message, { type: "error" });
        }
      }
      alert.show(t("budget_saved"), { type: "success" });
      onSaved(response.budget.id);
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlideOver
      open={open}
      title={budget ? t("manage_budget") : t("create_budget")}
      onClose={onClose}
      wide
    >
      <form className="budget-settings-form" onSubmit={submit}>
        <label>
          {t("budget_name")}
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <div className="budget-form-grid">
          <label>
            {t("start_date")}
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </label>
          <label>
            {t("end_date")}
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </label>
        </div>
        <section className="planned-category-section">
          <div className="planned-category-heading">
            <div>
              <h3>{t("planned_expenses")}</h3>
              <p>{t("planned_expenses_help")}</p>
            </div>
            <div className="planned-total">
              <span>{t("planned_total")}</span>
              <strong>{formatMoney(plannedTotalCents)}</strong>
            </div>
          </div>
          <div className="planned-category-grid">
            {form.categories.map((category) => (
              <label className="planned-category-row" key={category.id}>
                <span>
                  <i style={{ background: category.color }} />
                  {category.name}
                </span>
                <div className="planned-money-input">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={form.plannedAmountsByCategory[category.id] || ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        plannedAmountsByCategory: {
                          ...current.plannedAmountsByCategory,
                          [category.id]: event.target.value,
                        },
                      }))
                    }
                    aria-label={`${t("planned_amount")} — ${category.name}`}
                    placeholder="0,00"
                  />
                  <span>€</span>
                </div>
              </label>
            ))}
          </div>
        </section>
        <section className="source-picker">
          <h3>{t("included_pos")}</h3>
          <p>{t("included_pos_help")}</p>
          {organizations.map((org) => (
            <label className="source-option" key={org.id}>
              <input
                type="checkbox"
                checked={form.includedPosOrgIds.includes(org.id)}
                onChange={() => toggle("includedPosOrgIds", org.id)}
              />
              <span>
                <strong>{org.organization}</strong>
                <small>{t("pos_source")}</small>
              </span>
            </label>
          ))}
        </section>
        <section className="source-picker">
          <h3>{t("included_events")}</h3>
          <p>{t("included_events_help")}</p>
          {organizations.map((org) => {
            const orgEvents = events.filter((item) => item.orgId === org.id);
            if (!orgEvents.length) return null;
            return (
              <div className="event-group" key={org.id}>
                <strong>{org.organization}</strong>
                {orgEvents.map((event) => (
                  <label className="source-option" key={event.id}>
                    <input
                      type="checkbox"
                      checked={form.includedEventIds.includes(event.id)}
                      onChange={() => toggle("includedEventIds", event.id)}
                    />
                    <span>
                      <strong>{event.name || event.title}</strong>
                      <small>{t("ticketing_source")}</small>
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
        </section>
        <footer>
          <button type="button" className="secondary-button" onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="submit" className="primary-button" disabled={saving}>
            {saving ? t("saving") : t("save")}
          </button>
        </footer>
      </form>
    </SlideOver>
  );
}

export default function BudgetDashboard({ org }) {
  const { t } = useTranslation("budget");
  const alert = useAlert();
  const orgId = org?.id || "";
  const budgetRequestRef = useRef(0);
  const dashboardRequestRef = useRef(0);
  const [budgets, setBudgets] = useState([]);
  const [budgetId, setBudgetId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [sources, setSources] = useState({ organizations: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [budgetsLoading, setBudgetsLoading] = useState(true);
  const [entryDrawer, setEntryDrawer] = useState({ open: false, entry: null });
  const [budgetDrawer, setBudgetDrawer] = useState(false);
  const [budgetToEdit, setBudgetToEdit] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [entrySort, setEntrySort] = useState("date-desc");
  const [entryCategory, setEntryCategory] = useState("all");
  const [deletingCategoryId, setDeletingCategoryId] = useState("");
  const [retryingConsolidation, setRetryingConsolidation] = useState(false);

  useEffect(() => {
    budgetRequestRef.current += 1;
    dashboardRequestRef.current += 1;
    setBudgets([]);
    setBudgetId("");
    setDashboard(null);
    setSources({ organizations: [], events: [] });
    setFilter("all");
    setSearch("");
    setEntryCategory("all");
    setDeletingCategoryId("");
  }, [orgId]);

  const loadBudgets = useCallback(
    async (preferredId) => {
      if (!orgId) {
        setBudgets([]);
        setDashboard(null);
        setBudgetsLoading(false);
        setLoading(false);
        return;
      }
      const requestId = ++budgetRequestRef.current;
      setLoading(true);
      setBudgetsLoading(true);
      try {
        const [budgetResponse, sourceResponse] = await Promise.all([
          getBudgets(orgId),
          getBudgetSources(orgId),
        ]);
        if (requestId !== budgetRequestRef.current) return;
        setBudgets(budgetResponse.budgets);
        setSources(sourceResponse);
        setBudgetId(preferredId || budgetResponse.budgets[0]?.id || "");
      } catch (error) {
        if (requestId !== budgetRequestRef.current) return;
        alert.show(error.message, { type: "error" });
      } finally {
        if (requestId === budgetRequestRef.current) {
          setBudgetsLoading(false);
          setLoading(false);
        }
      }
    },
    [orgId, alert],
  );

  const loadDashboard = useCallback(
    async (targetBudgetId = budgetId) => {
      if (!targetBudgetId) {
        setDashboard(null);
        return;
      }
      const requestId = ++dashboardRequestRef.current;
      setLoading(true);
      try {
        const nextDashboard = await getBudgetDashboard(targetBudgetId);
        if (requestId === dashboardRequestRef.current) {
          setDashboard(nextDashboard);
        }
      } catch (error) {
        if (requestId !== dashboardRequestRef.current) return;
        alert.show(error.message, { type: "error" });
      } finally {
        if (requestId === dashboardRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [budgetId, alert],
  );

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const budget = dashboard?.budget || budgets.find((item) => item.id === budgetId);
  const consolidation = dashboard?.consolidation || budget?.consolidation || null;

  useEffect(() => {
    if (!budgetId || !["queued", "running"].includes(consolidation?.status)) return undefined;
    const timer = window.setTimeout(() => loadDashboard(budgetId), 5000);
    return () => window.clearTimeout(timer);
  }, [budgetId, consolidation?.status, consolidation?.jobId, loadDashboard]);

  const retryConsolidation = async () => {
    if (!budget) return;
    setRetryingConsolidation(true);
    try {
      await rebuildBudgetRevenues(budget.id, {});
      await loadDashboard(budget.id);
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setRetryingConsolidation(false);
    }
  };

  const categories = useMemo(() => budget?.categories || [], [budget?.categories]);
  const categoryMap = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories],
  );
  const activeEntryCategory =
    entryCategory === "all" || categoryMap.has(entryCategory) ? entryCategory : "all";
  const entries = useMemo(() => dashboard?.entries || [], [dashboard?.entries]);
  const filteredEntries = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    const visibleEntries = entries.filter((entry) => {
      if (filter !== "all" && entry.type !== filter) return false;
      if (activeEntryCategory !== "all" && entry.categoryId !== activeEntryCategory) return false;
      if (!normalized) return true;
      return (
        entry.providerName?.toLowerCase().includes(normalized) ||
        categoryMap.get(entry.categoryId)?.name?.toLowerCase().includes(normalized)
      );
    });

    return visibleEntries.sort((entryA, entryB) => {
      if (entrySort === "date-desc") return Number(entryB.date) - Number(entryA.date);

      const categoryA = categoryMap.get(entryA.categoryId)?.name || entryA.categoryId || "";
      const categoryB = categoryMap.get(entryB.categoryId)?.name || entryB.categoryId || "";
      const categoryComparison = categoryA.localeCompare(categoryB, undefined, {
        sensitivity: "base",
      });

      if (categoryComparison !== 0) {
        return entrySort === "category-desc" ? -categoryComparison : categoryComparison;
      }
      return Number(entryB.date) - Number(entryA.date);
    });
  }, [entries, filter, search, categoryMap, entrySort, activeEntryCategory]);

  const revenueSourcesByOrganization = useMemo(() => {
    const organizationsById = new Map(
      sources.organizations.map((organization) => [String(organization.id), organization]),
    );
    const eventsById = new Map(sources.events.map((event) => [String(event.id), event]));
    const totals = new Map();
    let manualRevenue = 0;
    (dashboard?.revenues || []).forEach((revenue) => {
      const amount = Number(revenue.totalAmountCents || 0);
      if (revenue.sourceType === "manual") {
        manualRevenue += amount;
        return;
      }
      if (!["pos", "ticketing"].includes(revenue.sourceType)) return;

      const event =
        revenue.sourceType === "ticketing" ? eventsById.get(String(revenue.sourceId)) : null;
      const organizationId = String(
        revenue.sourceOrgId || event?.orgId || revenue.orgId || revenue.sourceId || "unknown",
      );
      const organization = organizationsById.get(organizationId);
      const current = totals.get(organizationId) || {
        id: organizationId,
        name: organization?.organization || organization?.name || organizationId,
        pos: 0,
        ticketing: 0,
        total: 0,
      };
      current[revenue.sourceType] += amount;
      current.total += amount;
      totals.set(organizationId, current);
    });
    return {
      organizations: [...totals.values()].sort((a, b) => b.total - a.total),
      manualRevenue,
    };
  }, [dashboard?.revenues, sources.events, sources.organizations]);

  const monthlyData = useMemo(() => {
    const map = new Map();
    entries
      .filter((entry) => entry.type === "expense")
      .forEach((entry) => {
        const key = monthKey(new Date(entry.date));
        const item = map.get(key) || { month: key, expense: 0, revenue: 0 };
        item.expense += Number(entry.totalAmountCents || 0) / 100;
        map.set(key, item);
      });
    (dashboard?.revenues || []).forEach((revenue) => {
      const revenueDate = new Date(Number(revenue.timestamp));
      if (Number.isNaN(revenueDate.getTime())) return;
      const key = monthKey(revenueDate);
      const item = map.get(key) || { month: key, expense: 0, revenue: 0 };
      item.revenue += Number(revenue.totalAmountCents || 0) / 100;
      map.set(key, item);
    });
    const plannedTotal =
      Object.values(budget?.plannedAmountsByCategory || {}).reduce(
        (sum, value) => sum + Number(value || 0),
        0,
      ) / 100;
    const start = new Date(budget?.startDate || Date.now());
    const end = new Date(budget?.endDate || Date.now());
    const sorted = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      const key = monthKey(cursor);
      sorted.push(map.get(key) || { month: key, expense: 0, revenue: 0 });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    let cumulativeExpenses = 0;
    let cumulativeRevenue = 0;
    const chartData = sorted.map((item, index) => {
      cumulativeExpenses += item.expense;
      cumulativeRevenue += item.revenue;
      return {
        ...item,
        label: new Date(`${item.month}-01`).toLocaleDateString("fr-BE", {
          month: "short",
          ...(sorted.length > 12 ? { year: "2-digit" } : {}),
        }),
        planned: sorted.length ? (plannedTotal / sorted.length) * (index + 1) : 0,
        cumulativeExpenses,
        cumulativeRevenue,
      };
    });
    const realizedExpenseTotal = Number(dashboard?.summary?.expenseTotalCents || 0) / 100;
    if (chartData.length && Number.isFinite(realizedExpenseTotal)) {
      const lastIndex = chartData.length - 1;
      chartData[lastIndex] = {
        ...chartData[lastIndex],
        cumulativeExpenses: realizedExpenseTotal,
      };
    }
    return chartData;
  }, [
    entries,
    dashboard?.revenues,
    dashboard?.summary?.expenseTotalCents,
    budget?.plannedAmountsByCategory,
    budget?.startDate,
    budget?.endDate,
  ]);

  const categoryRows = useMemo(
    () =>
      categories
        .map((category) => {
          const planned = Number(budget?.plannedAmountsByCategory?.[category.id] || 0);
          const actual = entries
            .filter((entry) => entry.type === "expense" && entry.categoryId === category.id)
            .reduce((sum, entry) => sum + Number(entry.totalAmountCents || 0), 0);
          return { ...category, planned, actual, difference: planned - actual };
        })
        .filter((row) => row.planned || row.actual || isCustomCategory(row)),
    [categories, budget?.plannedAmountsByCategory, entries],
  );

  const refresh = async () => {
    setEntryDrawer({ open: false, entry: null });
    await loadDashboard();
  };

  const removeCategory = async (category) => {
    if (!window.confirm(t("confirm_delete_category", { category: category.name }))) return;
    setDeletingCategoryId(category.id);
    try {
      const response = await deleteBudgetCategory(budget.id, category.id);
      if (entryCategory === category.id) setEntryCategory("all");
      alert.show(t("category_deleted", { count: response.transferredEntries }), {
        type: "success",
      });
      await loadDashboard();
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setDeletingCategoryId("");
    }
  };

  const plannedExpenseTotal = Object.values(budget?.plannedAmountsByCategory || {}).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  );
  const budgetGap = plannedExpenseTotal - Number(dashboard?.summary?.expenseTotalCents || 0);

  const exportEntries = () => {
    const header = ["Date", "Type", "Libellé", "Catégorie", "Créateur", "Montant"];
    const rows = entries.map((entry) => [
      new Date(entry.date).toLocaleDateString("fr-BE"),
      t(entry.type),
      entry.providerName,
      categoryMap.get(entry.categoryId)?.name || entry.categoryId,
      entry.creatorName,
      (entry.totalAmountCents / 100).toFixed(2),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`).join(";"))
      .join("\n");
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${budget?.name || "budget"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!org?.id) return null;
  return (
    <main className="budget-page">
      {budgetsLoading ? (
        <div className="budget-loading-overlay" role="status" aria-live="polite">
          <span className="budget-loading-spinner" aria-hidden="true" />
          <strong>{t("loading_budgets")}</strong>
          <small>{t("loading_budgets_help")}</small>
        </div>
      ) : null}
      <header className="budget-page-header">
        <div>
          <h1>{t("title")}</h1>
          <p>{t("subtitle")}</p>
        </div>
        <div className="budget-page-actions">
          <div className="budget-selection-controls">
            {budgets.length ? (
              <select value={budgetId} onChange={(event) => setBudgetId(event.target.value)}>
                {budgets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              className="secondary-button"
              onClick={() => {
                setBudgetToEdit(null);
                setBudgetDrawer(true);
              }}
            >
              <Icon name="plus-circle" /> {t("create_budget")}
            </button>
          </div>
          <div className="budget-operation-controls">
            {budget ? (
              <span className="budget-date-control">
                <Icon name="calendar-alt" />
                {new Date(budget.startDate).toLocaleDateString("fr-BE")} —{" "}
                {new Date(budget.endDate).toLocaleDateString("fr-BE")}
              </span>
            ) : null}
            {budget ? (
              <button
                className="secondary-button"
                onClick={() => {
                  setBudgetToEdit(budget);
                  setBudgetDrawer(true);
                }}
              >
                <Icon name="sliders-h" /> {t("manage_budget")}
              </button>
            ) : null}
            <button
              className="primary-button"
              disabled={!budget}
              onClick={() => setEntryDrawer({ open: true, entry: null })}
            >
              <Icon name="plus" /> {t("new_entry")}
            </button>
            <button className="secondary-button" disabled={!budget} onClick={exportEntries}>
              <Icon name="download" /> {t("export")}
            </button>
          </div>
        </div>
      </header>

      {!budget && !loading ? (
        <section className="budget-empty">
          <span>
            <Icon name="chart-line" />
          </span>
          <h2>{t("empty_title")}</h2>
          <p>{t("empty_text")}</p>
          <button
            className="primary-button"
            onClick={() => {
              setBudgetToEdit(null);
              setBudgetDrawer(true);
            }}
          >
            {t("create_budget")}
          </button>
        </section>
      ) : (
        <>
          {budget && ["queued", "running"].includes(consolidation?.status) ? (
            <section className="budget-consolidation-banner" role="status">
              <span className="budget-consolidation-spinner" aria-hidden="true" />
              <div>
                <strong>{t("consolidation_in_progress")}</strong>
                <p>{t("consolidation_in_progress_help")}</p>
              </div>
            </section>
          ) : null}
          {budget && consolidation?.status === "failed" ? (
            <section className="budget-consolidation-banner is-error" role="alert">
              <div>
                <strong>{t("consolidation_failed")}</strong>
                <p>{t("consolidation_failed_help")}</p>
              </div>
              <button
                className="secondary-button"
                onClick={retryConsolidation}
                disabled={retryingConsolidation}
              >
                {retryingConsolidation ? t("consolidation_retrying") : t("consolidation_retry")}
              </button>
            </section>
          ) : null}
          <section className="budget-summary-strip" aria-busy={loading}>
            <Metric
              label={t("revenue_realized")}
              value={formatMoney(dashboard?.summary?.revenueTotalCents)}
              tone="income"
              icon="chart-line"
            />
            <Metric
              label={t("expense_realized")}
              value={formatMoney(dashboard?.summary?.expenseTotalCents)}
              tone="expense"
              icon="arrow-down"
            />
            <Metric
              label={t("result")}
              value={formatMoney(dashboard?.summary?.resultCents)}
              tone="result"
              icon="balance-scale"
            />
            <Metric
              label={t("budget_gap")}
              value={formatMoney(budgetGap)}
              tone="period"
              icon="bullseye"
            />
          </section>

          <section className="budget-analysis-grid">
            <article className="budget-panel budget-chart-panel">
              <div className="panel-heading">
                <div>
                  <h2>{t("forecast_vs_actual")}</h2>
                  <p>{t("forecast_help")}</p>
                </div>
              </div>
              <div className="budget-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyData}>
                    <CartesianGrid stroke="rgba(139,164,191,.12)" vertical={false} />
                    <XAxis dataKey="label" stroke="#8ba4bf" tickLine={false} axisLine={false} />
                    <YAxis
                      stroke="#8ba4bf"
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#111d3b",
                        border: "1px solid rgba(139,164,191,.24)",
                        borderRadius: 10,
                      }}
                      formatter={(value) => euro.format(value)}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11, color: "#9eb0c8" }} />
                    <Line
                      type="monotone"
                      dataKey="planned"
                      name={`${t("forecast")} (${t("cumulative")})`}
                      stroke="#6688ff"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cumulativeExpenses"
                      name={t("cumulative_expenses")}
                      stroke="#ff6da8"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cumulativeRevenue"
                      name={t("cumulative_revenue")}
                      stroke="#41c98e"
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </article>
            <article className="budget-panel budget-source-panel">
              <div className="panel-heading">
                <div>
                  <h2>{t("revenue_sources")}</h2>
                  <p>{t("revenue_sources_help")}</p>
                </div>
              </div>
              <div className="source-comparison" role="table" aria-label={t("revenue_sources")}>
                <div className="source-comparison-header" role="row">
                  <span role="columnheader">{t("organization")}</span>
                  <span role="columnheader">
                    <i style={{ background: SOURCE_COLORS.pos }} />
                    {t("source_pos")}
                  </span>
                  <span role="columnheader">
                    <i style={{ background: SOURCE_COLORS.ticketing }} />
                    {t("source_ticketing")}
                  </span>
                  <span role="columnheader">{t("total")}</span>
                </div>
                {revenueSourcesByOrganization.organizations.map((source) => (
                  <div className="source-comparison-row" role="row" key={source.id}>
                    <strong role="cell">{source.name}</strong>
                    <span role="cell">{formatMoney(source.pos)}</span>
                    <span role="cell">{formatMoney(source.ticketing)}</span>
                    <em role="cell">{formatMoney(source.total)}</em>
                  </div>
                ))}
                {revenueSourcesByOrganization.manualRevenue ? (
                  <div className="source-comparison-row manual" role="row">
                    <strong role="cell">{t("source_manual")}</strong>
                    <span role="cell">—</span>
                    <span role="cell">—</span>
                    <em role="cell">{formatMoney(revenueSourcesByOrganization.manualRevenue)}</em>
                  </div>
                ) : null}
                {!revenueSourcesByOrganization.organizations.length &&
                !revenueSourcesByOrganization.manualRevenue ? (
                  <p className="source-comparison-empty">{t("no_revenue_sources")}</p>
                ) : null}
              </div>
              <div className="source-footer">
                <span>{t("total_revenue")}</span>
                <strong>{formatMoney(dashboard?.summary?.revenueTotalCents)}</strong>
              </div>
            </article>
          </section>

          <section className="budget-panel budget-category-panel">
            <div className="panel-heading">
              <div>
                <h2>{t("category_tracking")}</h2>
                <p>{t("category_tracking_help")}</p>
              </div>
            </div>
            <div className="budget-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("category")}</th>
                    <th>{t("forecast")}</th>
                    <th>{t("actual")}</th>
                    <th>{t("difference")}</th>
                    <th>{t("progress")}</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryRows.length ? (
                    categoryRows.map((row) => {
                      const progress = row.planned
                        ? Math.round((row.actual / row.planned) * 100)
                        : 0;
                      return (
                        <tr key={row.id}>
                          <td>
                            <span className="category-name-cell">
                              <span className="category-dot" style={{ background: row.color }} />
                              <span>{row.name}</span>
                              {isCustomCategory(row) ? (
                                <button
                                  type="button"
                                  className="category-delete-button"
                                  disabled={deletingCategoryId === row.id}
                                  onClick={() => removeCategory(row)}
                                  title={t("delete_custom_category")}
                                  aria-label={t("delete_custom_category")}
                                >
                                  <Icon name="trash-alt" />
                                </button>
                              ) : null}
                            </span>
                          </td>
                          <td>{formatMoney(row.planned)}</td>
                          <td>{formatMoney(row.actual)}</td>
                          <td className={row.difference >= 0 ? "positive" : "negative"}>
                            {formatMoney(row.difference)}
                          </td>
                          <td>
                            <div className="progress-cell">
                              <div>
                                <span
                                  className={progress > 100 ? "over" : ""}
                                  style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                              </div>
                              <em>{progress}%</em>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-row">
                        {t("no_category_data")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="budget-panel budget-entries-panel">
            <div className="panel-heading entries-heading">
              <div>
                <h2>{t("entries")}</h2>
                <p>{t("entries_help")}</p>
              </div>
              <div className="entry-filters">
                <div className="search-field">
                  <Icon name="search" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("search")}
                  />
                </div>
                <div className="filter-switch">
                  {["all", "revenue", "expense"].map((value) => (
                    <button
                      key={value}
                      className={filter === value ? "active" : ""}
                      onClick={() => setFilter(value)}
                    >
                      {t(value)}
                    </button>
                  ))}
                </div>
                <div className="entry-sort">
                  <Icon name="sort-amount-down" />
                  <select
                    value={entrySort}
                    onChange={(event) => setEntrySort(event.target.value)}
                    aria-label={t("sort_entries")}
                    title={t("sort_entries")}
                  >
                    <option value="date-desc">{t("sort_recent")}</option>
                    <option value="category-asc">{t("sort_category_asc")}</option>
                    <option value="category-desc">{t("sort_category_desc")}</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="entry-category-strip" aria-label={t("filter_by_category")}>
              <button
                type="button"
                className={`entry-category-filter ${activeEntryCategory === "all" ? "active" : ""}`}
                onClick={() => setEntryCategory("all")}
                aria-pressed={activeEntryCategory === "all"}
              >
                <span className="category-dot category-dot-all" aria-hidden="true" />
                {t("all_categories")}
              </button>
              {categories.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  className={`entry-category-filter ${activeEntryCategory === category.id ? "active" : ""}`}
                  style={{ "--entry-category-color": category.color || "#7c8eaa" }}
                  onClick={() => setEntryCategory(category.id)}
                  aria-pressed={activeEntryCategory === category.id}
                >
                  <span
                    className="category-dot"
                    style={{ backgroundColor: category.color || "#7c8eaa" }}
                    aria-hidden="true"
                  />
                  {category.name}
                </button>
              ))}
            </div>
            <div className="budget-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("date")}</th>
                    <th>{t("label")}</th>
                    <th>{t("category")}</th>
                    <th>{t("creator")}</th>
                    <th>{t("attachment")}</th>
                    <th>{t("amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.length ? (
                    filteredEntries.map((entry) => {
                      const category = categoryMap.get(entry.categoryId);
                      return (
                        <tr
                          key={entry.id}
                          onClick={() => setEntryDrawer({ open: true, entry })}
                          className="clickable-row"
                        >
                          <td>{new Date(entry.date).toLocaleDateString("fr-BE")}</td>
                          <td>
                            <strong>{entry.providerName}</strong>
                            <small>{t(entry.type)}</small>
                          </td>
                          <td>
                            <span className="entry-category">
                              <span
                                className="category-dot"
                                style={{ backgroundColor: category?.color || "#7c8eaa" }}
                                aria-hidden="true"
                              />
                              <span>{category?.name || entry.categoryId}</span>
                            </span>
                          </td>
                          <td>{entry.creatorName}</td>
                          <td>{entry.attachment ? <Icon name="paperclip" /> : "—"}</td>
                          <td className={entry.type === "expense" ? "negative" : "positive"}>
                            {entry.type === "expense" ? "−" : "+"}
                            {formatMoney(entry.totalAmountCents)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="empty-row">
                        {t("no_entries")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <EntryForm
        open={entryDrawer.open}
        entry={entryDrawer.entry}
        budget={budget}
        categories={categories}
        onClose={() => setEntryDrawer({ open: false, entry: null })}
        onSaved={refresh}
        onCategoriesChanged={loadDashboard}
      />
      <BudgetForm
        open={budgetDrawer}
        budget={budgetToEdit}
        ownerOrgId={org.id}
        organizations={sources.organizations}
        events={sources.events}
        onClose={() => {
          setBudgetDrawer(false);
          setBudgetToEdit(null);
        }}
        onSaved={async (id) => {
          setBudgetDrawer(false);
          setBudgetToEdit(null);
          await loadBudgets(id);
          await loadDashboard(id);
        }}
      />
    </main>
  );
}
